import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useRef, useEffect, useState, useContext, useCallback } from "react";
import { ThemeContext } from "../../../../theme/ThemeContext";
import TicketDashboardService from "../../../../services/TicketDashboardService";
import AuthContext from "../../../../auth/AuthContext";
import AlertService from "../../../../services/AlertService";
import { Skeleton } from 'primereact/skeleton';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function TicketRaisedChart() {
  const authContext = useContext(AuthContext);
  const chartRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const [courseId, setCourseId] = useState(0);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Tickets",
        data: [],
        backgroundColor: "rgba(90,87,255,0.8)",
        borderRadius: 20,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
    ],
  });

const loadCourses = useCallback(async (signal) => { 
    try {
      const data = await TicketDashboardService.GetCourses(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setCourses(data.data);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    }
  }, [authContext]);

const loadDashboard = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await TicketDashboardService.GetTicketDashboard(
        courseId,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        const counts = data.data.map(m => m.count);
        const maxCount = Math.max(...counts, 40);
        const stepSize = Math.ceil(maxCount / 5);
        const maxTicks = stepSize * 5;

        setChartData({
          labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
          datasets: [
            {
              label: "Tickets",
              data: counts,
              backgroundColor: "rgba(90,87,255,0.8)",
              borderRadius: 20,
              barPercentage: 0.6,
              categoryPercentage: 0.8,
            },
          ],
          maxTicks,
          stepSize
        });
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, courseId]);

useEffect(() => {
    const controller = new AbortController(); 
    loadCourses(controller.signal);
    loadDashboard(controller.signal);
    return () => controller.abort();
  }, [loadCourses, loadDashboard]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const ctx = chart.ctx;
    const gradient = ctx.createLinearGradient(0, 0, 0, 260);
    gradient.addColorStop(0, "#5A57FF");
    gradient.addColorStop(1, "rgba(90, 86, 184, 0.75)");

    setChartData((prevData) => {
      const newData = { ...prevData };
      if (newData.datasets[0]) {
        newData.datasets[0].backgroundColor = gradient;
      }
      return newData;
    });
  }, []);

  const yAxisMax = chartData.maxTicks || 40;
  const yAxisStepSize = chartData.stepSize || 10;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        titleFont: { family: "Poppins", size: 13, weight: "600" },
        bodyFont: { family: "Poppins", size: 12 }
      },
    },
    scales: {
      x: {
        ticks: {
          color: isDark ? "#eaeaea" : "#444444",
          font: { size: 12, weight: 600, family: "Poppins" },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        min: 0,
        max: yAxisMax,
        ticks: {
          color: isDark ? "#eaeaea" : "#444444",
          font: { size: 12, family: "Poppins" },
          stepSize: yAxisStepSize,
          callback: function (value) {
            return Number.isInteger(value) ? value : null;
          }
        },
        grid: { color: isDark ? "#444444" : "#f1f1f1" },
      },
    },
  };

  return (
    <div
      className="charts d-flex flex-column"
      style={{
        padding: 16,
        minHeight: "337px",
        width: "100%",
        border: "1px solid #eee",
        borderRadius: "12px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        {loading ? (
          <>
            <Skeleton width="150px" height="24px" />
            <Skeleton width="100px" height="32px" />
          </>
        ) : (
          <>
            <h6
              style={{
                margin: 0,
                fontSize: 16,
              }}
            >
              Tickets Raised
            </h6>
            <select
              style={{
                border: "1px solid #ddd",
                borderRadius: 6,
                backgroundColor: isDark ? "#222" : "#fff",
                color: isDark ? "#eaeaea" : "#000",
                padding: "4px 8px",
                fontSize: 13,
                maxWidth: "140px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              onChange={(e) => setCourseId(parseInt(e.target.value))}
              value={courseId}
              title={courses.find(c => c.value === courseId)?.label || "All"}
            >
              <option value={0}>All Courses</option>
              {courses.map(m => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

          </>
        )}
      </div>
      <div style={{ flexGrow: 1, position: "relative" }}>
        {loading ? (
          <Skeleton height="250px" />
        ) : (
          <Bar ref={chartRef} data={chartData} options={options} />
        )}
      </div>
    </div>
  );
}
